import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import uploadIcon from 'assets/img/upload-img.svg';
import {
  selectLoader,
  extractScopeFromContract,
  validateMilestones,
  validateMilestonesForScope,
  getDateFormat
} from 'utils/Helpers';
import appToast, { TOAST_TYPE } from 'utils/AppToast';
import uploadIcon2 from 'assets/img/upload-icon2.svg';
import extractedIcon from 'assets/img/extracted-icon.svg';
import rightArrowIcon from 'assets/img/right-arrow.svg';
import deleteIcon from 'assets/img/delete.svg';
import DetailsTab from 'pages/projects/overviewtabs/DetailsTab';
import { useStore } from 'hooks/useStore';

const UploadContractContent = ({
  showDetails = false,
  onConfirm,
  organizationsList,
  setInternalProject,
  isInternalProject,
  isProjectSubmitting,
  initialFile = null,
  initialScope = '',
  initialMilestones = [],
  initialProjectData = null,
  setScopeText = () => {},
  setMilestonesData = () => {},
  setProjectDetailsData = () => {},
  setContractPrefill
}) => {
  const isExistingProjectFlow = Boolean(initialFile);
  const detailsRef = useRef();
  const [store, setStore] = useStore();
  const [loading, setLoading] = useState(false);
  // const [step, setStep] = useState('upload');
  const [scope, setScope] = useState(initialScope);
  const [milestones, setMilestones] = useState(initialMilestones);
  const [contractFile, setContractFile] = useState(null);
  const [projectDetails, setProjectDetails] = useState(initialProjectData || {});
  const [invalidMilestones, setInvalidMilestones] = useState([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setProjectDetailsData(projectDetails);
    }, 300);

    return () => clearTimeout(timeout);
  }, [projectDetails]);

  useEffect(() => {
    setInvalidMilestones([]);
  }, [milestones, projectDetails?.endDate]);

  useEffect(() => {
    if (initialFile && initialScope && initialMilestones.length) {
      setContractFile(initialFile);
      // setStep('preview');
    }
  }, [initialFile, initialMilestones]);

  useEffect(() => {
    if (initialProjectData) {
      setProjectDetails(initialProjectData);
    }
  }, [initialProjectData]);

  useEffect(() => {
    if (initialScope) {
      setScope(initialScope);
    }
  }, [initialScope]);

  useEffect(() => {
    if (initialMilestones?.length) {
      setMilestones(initialMilestones);
    }
  }, [initialMilestones]);

  const [submitAttempted, setSubmitAttempted] = useState(false);

  const validateMilestoneDatesWithProject = () => {
    if (!projectDetails?.endDate) return true;

    const endDate = new Date(projectDetails.endDate);

    let invalidIndexes = [];

    milestones.forEach((m, index) => {
      if (m.dueDate) {
        const due = new Date(m.dueDate);

        if (due > endDate) {
          invalidIndexes.push(index);
        }
      }
    });

    if (invalidIndexes.length > 0) {
      setInvalidMilestones(invalidIndexes);

      appToast('Milestone due date cannot be after project end date.', TOAST_TYPE.ERROR);

      return false;
    }

    setInvalidMilestones([]);
    return true;
  };

  const handleMilestoneChange = (index, field, value) => {
    const updated = [...milestones];

    if (field === 'dueDate' && value) {
      const date = new Date(value);

      const normalizedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));

      updated[index][field] = normalizedDate.toISOString();
    } else {
      updated[index][field] = value;
    }

    setMilestones(updated);
    setMilestonesData(updated); // 👈 sync to parent

    if (submitAttempted) {
      setSubmitAttempted(false);
    }
  };

  const addMilestone = () => {
    setMilestones([...milestones, { name: '', paymentDue: 0, dueDate: '' }]);
    setMilestonesData([...milestones, { name: '', paymentDue: 0, dueDate: '' }]);
  };

  const deleteMilestone = (index) => {
    if (milestones.length === 1) {
      appToast('At least one milestone is required.', TOAST_TYPE.ERROR);
      return;
    }

    const updated = milestones.filter((_, i) => i !== index);
    setMilestones(updated);
    setMilestonesData(updated); // 👈 sync to parent

    if (submitAttempted) {
      setSubmitAttempted(false);
    }
  };

  return (
    <div className="contract-preview-wrapper">
      {/* Scope Section */}
      {showDetails && (
        <div className="preview-card create_project preview-details">
          <DetailsTab
            ref={detailsRef}
            isModal={true}
            initialProjectData={projectDetails}
            organizationsList={organizationsList}
            setInternalProject={setInternalProject}
            isInternalProject={isInternalProject}
            milestones={milestones}
            setProjectDetails={setProjectDetails}
          />
        </div>
      )}
      <div className="preview-card">
        <div className="preview-header">
          <p>Scope of Work</p>
          <span className="extracted-badge">
            {' '}
            <img src={extractedIcon} alt="extractedIcon" width={40} className="extractedIcon" /> Extracted from contract
          </span>
        </div>

        <div className="quill-resizable-wrapper">
          <ReactQuill
            value={scope}
            onChange={(val) => {
              setScope(val);
              setScopeText(val);
            }}
            className="contract-quill-editor resizable-quill"
          />
        </div>
      </div>

      {/* Milestones Section */}
      {milestones.length > 0 && (
        <>
          <div className="milestone-box-cover">
            <img src={extractedIcon} alt="extractedIcon" width={40} className="extractedIcon2" />
            <div className="milestone-box-content">
              <div className="milestone-banner">{milestones.length} milestones extracted from your contract</div>
              <p>Review and adjust the milestones below. You can edit any details or add new milestones.</p>
            </div>
          </div>
          <div className="preview-milestones-overflow">
            <div className="preview-milestones">
              {milestones.map((milestone, index) => (
                <div key={index} className="milestone-row-editable">
                  <div className="milestone-number">
                    <span>{index + 1}</span>
                  </div>
                  <div className={`form-control w-320 ${submitAttempted && !milestone.name ? 'form-invalid' : ''}`}>
                    <input
                      type="text"
                      value={milestone.name}
                      onChange={(e) => handleMilestoneChange(index, 'name', e.target.value)}
                      className={`milestone-input name `}
                      placeholder="Milestone name"
                    />
                  </div>
                  <div
                    className={`form-control w-210 ${submitAttempted && !milestone.paymentDue ? 'form-invalid' : ''}`}>
                    <label className="label-title">Payment Due</label>
                    <input
                      type="text"
                      value={milestone.paymentDue ? `$ ${Number(milestone.paymentDue).toLocaleString()}` : ''}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, '');
                        handleMilestoneChange(index, 'paymentDue', raw);
                      }}
                      className="milestone-input payment"
                      placeholder="$ 0"
                    />
                  </div>
                  <div
                    className={`form-control w-190 
    ${submitAttempted && !milestone.dueDate ? 'form-invalid' : ''}
    ${invalidMilestones.includes(index) ? 'form-invalid' : ''}
  `}>
                    <label className="label-title">Due date</label>
                    {/* {console.log(milestone)} */}

                    <DatePicker
                      selected={milestone.dueDate ? new Date(milestone.dueDate) : null}
                      onChange={(date) => handleMilestoneChange(index, 'dueDate', date)}
                      placeholderText="Select Date"
                      dateFormat={getDateFormat(store.currentDateFormat)}
                      className="milestone-input date"
                      popperPlacement="bottom"
                      onChangeRaw={(e) => e.preventDefault()}
                      onKeyDown={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      isClearable
                    />
                  </div>
                  <img
                    src={deleteIcon}
                    alt="deleteIcon"
                    width={40}
                    className="deleteIcon cursor_pointer"
                    onClick={() => deleteMilestone(index)}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <div className="btn-group-cover">
        <button type="button" className="btn add-new-milestone" onClick={addMilestone}>
          + Add Milestone
        </button>

        {isProjectSubmitting ? (
          <button className="btn btn-black">{selectLoader(20)}</button>
        ) : (
          <button
            className="btn btn-black"
            onClick={async () => {
              setSubmitAttempted(true);

              if (!validateMilestonesForScope(milestones)) return;

              if (!validateMilestoneDatesWithProject()) return;

              onConfirm(contractFile, scope, milestones);
            }}>
            Continue <img src={rightArrowIcon} alt="comp1" className="rightArrowIcon-arrow" />
          </button>
        )}
      </div>
    </div>
  );
};

export default UploadContractContent;
